import React, { useContext, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Modal } from "react-bootstrap";
import { AuthContext } from "../components/AuthContext";
import TrackerBulkCredits from "./TrackerBulkCredits";
import { catalogForTracker } from "./trackerScenarioCatalog";
import "./Tracker.css";

const normalized = (value) => String(value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
// Permite encontrar "pendiente imprimir" dentro de "Pendiente de imprimir".
const matchesCharacterSearch = (value, search) => {
  const words = normalized(search).split(/\s+/).filter(Boolean);
  const text = normalized(value);
  let from = 0;
  return words.every((word) => {
    const index = text.indexOf(word, from);
    if (index < 0) return false;
    from = index + word.length;
    return true;
  });
};
const numericCode = (season, number) => /^\d+$/.test(String(season ?? "")) && /^\d+$/.test(String(number ?? ""))
  ? `${Number(season)}-${String(number).padStart(2, "0")}` : "";
const xp = (value) => new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(Number(value));
let tokenRefresh;

async function trackerRequest(method, path, data) {
  const send = () => axios.request({ method, url: `${process.env.REACT_APP_API_URL}${path}`, data,
    headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` } });
  try {
    return (await send()).data;
  } catch (error) {
    if (error.response?.status !== 401 || !localStorage.getItem("refresh_token")) throw error;
    // Las cargas simultáneas comparten el refresco del token.
    if (!tokenRefresh) {
      tokenRefresh = axios.post(`${process.env.REACT_APP_API_URL}/token/refresh/`, {
        refresh: localStorage.getItem("refresh_token"),
      }).then(({ data: tokens }) => {
        localStorage.setItem("access_token", tokens.access);
        if (tokens.refresh) localStorage.setItem("refresh_token", tokens.refresh);
      }).finally(() => { tokenRefresh = null; });
    }
    await tokenRefresh;
    return (await send()).data;
  }
}

function errorMessage(error) {
  if (error.response?.status === 401) return "Tu sesión ha caducado. Vuelve a iniciar sesión.";
  const payload = error.response?.data;
  if (!payload) return "No se ha podido conectar. Vuelve a intentarlo.";
  if (typeof payload === "string") return "No se ha podido completar la operación.";
  return Object.values(payload).flat().join(" ");
}

function ScenarioField({ value, onChange, scenarios, disabled, invalid, keepOpenOnSelect = false }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const fieldId = React.useId();
  const inputRef = React.useRef(null);
  const selected = scenarios.find((item) => String(item.id) === String(value));
  const format = (item) => `#${item.season}-${item.number} · ${item.name}`;
  const matches = scenarios.filter((item) => {
    const q = normalized(query);
    if (!q) return true;
    const code = numericCode(item.season, item.number);
    return [item.name, item.season, item.number, format(item), code,
      code && `S${code}`, code && `S${code.split("-")[0].padStart(2, "0")}-${code.split("-")[1]}`]
      .some((part) => normalized(part).includes(q));
  });
  const options = matches.slice(0, 10);
  const choose = (item) => {
    onChange(String(item.id));
    setQuery(""); setActive(0);
    // En el alta múltiple se mantiene el buscador listo para la siguiente crónica.
    setOpen(keepOpenOnSelect);
    if (keepOpenOnSelect) inputRef.current?.focus();
  };
  return <div className="tracker-scenario-picker tracker-wide" onBlur={(event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) { setOpen(false); setQuery(""); }
  }}>
    <label htmlFor={fieldId}>Escenario</label>
    <div className="tracker-scenario-query">
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round"><circle cx="10.8" cy="10.8" r="6.8"/><path d="m16 16 5 5"/></svg>
      <input ref={inputRef} id={fieldId} type="search" role="combobox" aria-label="Buscar escenario"
        autoComplete="off" aria-autocomplete="list" aria-expanded={open}
        aria-controls={`${fieldId}-options`}
        aria-activedescendant={open && options[active] ? `${fieldId}-option-${options[active].id}` : undefined}
        aria-invalid={invalid || undefined}
        placeholder="Nombre o código, ej. 5-06"
        value={open ? query : selected ? format(selected) : ""}
        disabled={disabled}
        onFocus={() => { setOpen(true); setQuery(""); setActive(0); }}
        onChange={(event) => { setQuery(event.target.value); setActive(0); onChange(""); }}
        onKeyDown={(event) => {
          if (event.key === "Escape") { setOpen(false); setQuery(""); event.currentTarget.blur(); }
          if (event.key === "ArrowDown" && options.length) {
            event.preventDefault(); setOpen(true); setActive((index) => Math.min(index + 1, options.length - 1));
          }
          if (event.key === "ArrowUp" && options.length) {
            event.preventDefault(); setOpen(true); setActive((index) => Math.max(index - 1, 0));
          }
          if (event.key === "Enter" && open && options.length) {
            event.preventDefault(); choose(options[active] || options[0]);
          }
        }} />
    </div>
    {open && <div className="tracker-scenario-results">
      <div id={`${fieldId}-options`} role="listbox" aria-label="Escenarios encontrados">
        {options.map((item, index) => <button key={item.id} type="button" role="option"
          id={`${fieldId}-option-${item.id}`} aria-selected={index === active}
          className={index === active ? "tracker-scenario-active" : ""}
          disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => choose(item)}>
          <span className="tracker-scenario-code">#{item.season}-{item.number}</span>
          <span className="tracker-scenario-name">{item.name}</span>
        </button>)}
      </div>
      {!options.length && <p>No hay escenarios que coincidan.</p>}
    </div>}
    {invalid && <small className="tracker-field-error" role="alert">Selecciona un escenario de los resultados.</small>}
  </div>;
}

function CharacterForm({ character, paizoNumber = "", onSave, busy, formId }) {
  const currentId = String(character?.paizo_id || "");
  const originalNumber = currentId.split("-").pop();
  const [characterNumber, setCharacterNumber] = useState(originalNumber);
  const [form, setForm] = useState({
    name: character?.name || "",
    game: character?.game || "pfs2",
    character_class: character?.character_class || "",
    notes: character?.notes || "",
  });
  const change = (event) => setForm({ ...form, [event.target.name]: event.target.value });
  return <form id={formId} className="tracker-form tracker-character-form" onSubmit={async (event) => {
    event.preventDefault();
    if (!paizoNumber && !character) return;
    const payload = { ...form };
    // Actualizar el ID solo cuando el usuario cambia el número, nunca por editar otro campo.
    if (paizoNumber && (!character || characterNumber !== originalNumber)) {
      payload.paizo_id = characterNumber ? `${paizoNumber}-${characterNumber}` : "";
    }
    if (await onSave(payload)) {
      if (!character) { setForm({ name: "", game: "pfs2", character_class: "", notes: "" }); setCharacterNumber(""); }
    }
  }}>
    <label className="tracker-wide">Nombre del personaje
      <input name="name" value={form.name} onChange={change} required maxLength={255} disabled={busy} placeholder="Ej. Scath" />
    </label>
    <div className="tracker-paizo-field tracker-wide">
      <label htmlFor="tracker-character-number">Número PFS del personaje</label>
      {paizoNumber ? <div className="tracker-paizo-composed">
        <span className="tracker-paizo-prefix" aria-label={`Tu ID de Paizo: ${paizoNumber}`}>{paizoNumber}</span>
        <span className="tracker-paizo-separator" aria-hidden="true">-</span>
        <input id="tracker-character-number" name="character_number" type="text"
          inputMode="numeric" pattern="[0-9]+" maxLength={20} required={!character}
          value={characterNumber} onChange={(event) => setCharacterNumber(event.target.value.replace(/[^0-9]/g, ""))}
          disabled={busy} placeholder="2001" aria-label="Número de personaje" />
      </div> : <p className="tracker-paizo-unconfigured" role="status">
        Configura primero tu <Link to="/dashboard">ID de Paizo en Datos del usuario</Link>.
        {character && " Puedes guardar los demás campos sin cambiar su número PFS."}
      </p>}
      {paizoNumber && <small>Este prefijo procede de tus datos de usuario; solo puedes cambiar el número del personaje aquí.</small>}
    </div>
    <label className="tracker-wide">Sistema de juego
      <select name="game" value={form.game} onChange={change} disabled={busy}>
        <option value="pfs2">Pathfinder 2e</option>
        <option value="sfs2">Starfinder 2e</option>
      </select>
    </label>
    <label className="tracker-wide">Clase
      <input name="character_class" value={form.character_class} onChange={change} maxLength={255} disabled={busy} placeholder="Ej. Guerrero" />
    </label>
    <label className="tracker-wide">Notas
      <textarea name="notes" rows="2" value={form.notes} onChange={change} disabled={busy}
        placeholder="Información adicional del personaje..." />
    </label>
    {!formId && <button type="submit" disabled={busy || (!paizoNumber && !character)}>Guardar personaje</button>}
  </form>;
}

function CreditForm({ credit, scenarios, onSave, busy, formId }) {
  const [kind, setKind] = useState(credit?.kind || "scenario");
  const [scenario, setScenario] = useState(credit?.scenario || "");
  const [amount, setAmount] = useState(credit?.experience != null ? String(Number(credit.experience)) : "4");
  // Los créditos antiguos con XP fraccionaria se pueden editar sin cambiar su valor.
  const legacyFractionalXP = credit?.experience != null && !Number.isInteger(Number(credit.experience));
  const [notes, setNotes] = useState(credit?.notes || "");
  const [scenarioError, setScenarioError] = useState(false);
  return <form id={formId} className="tracker-form" onSubmit={async (event) => {
    event.preventDefault();
    if (kind === "scenario" && !scenario) { setScenarioError(true); return; }
    const numericAmount = Number(amount);
    const unchangedLegacyXP = legacyFractionalXP && numericAmount === Number(credit.experience);
    if (!amount || !Number.isFinite(numericAmount)
      || !(unchangedLegacyXP || (Number.isInteger(numericAmount) && numericAmount >= 1 && numericAmount <= 999999))) return;
    const experience = credit && Number(amount) === Number(credit.experience) ? credit.experience : amount;
    if (await onSave({ kind, scenario: kind === "initial" ? null : Number(scenario), experience, notes })) {
      if (!credit) { setScenario(""); setAmount("4"); setNotes(""); }
    }
  }}>
    <label className="tracker-wide">Tipo de crédito<select value={kind} onChange={(event) => { setKind(event.target.value); setScenarioError(false); }} disabled={busy}>
      <option value="scenario">Crédito de escenario</option><option value="initial">Experiencia inicial</option>
    </select></label>
    <small className="tracker-wide tracker-form-help">{kind === "initial"
      ? "XP que el personaje ya tenía antes de registrarlo en el tracker. No corresponde a una aventura; solo puede haber un crédito de experiencia inicial por personaje."
      : "XP obtenida en una aventura concreta. Selecciona el escenario al que corresponde."}</small>
    {kind === "scenario" && <ScenarioField value={scenario} invalid={scenarioError}
      onChange={(value) => { setScenario(value); setScenarioError(false); }} scenarios={scenarios} disabled={busy} />}
    <label className="tracker-field-xp">XP<input type="number" inputMode="numeric"
      min={legacyFractionalXP ? "0.01" : "1"} max={legacyFractionalXP ? "999999.99" : "999999"} step={legacyFractionalXP ? "any" : "1"}
      value={amount} onChange={(event) => setAmount(event.target.value)} required disabled={busy} /></label>
    <label className="tracker-wide">Notas del crédito<textarea rows="3" value={notes} onChange={(event) => setNotes(event.target.value)} disabled={busy} /></label>
  </form>;
}

function HistoryForm({ entry, scenarios, onSave, busy, formId, onRoleChange }) {
  const [scenario, setScenario] = useState(entry?.scenario || "");
  const [player, setPlayer] = useState(entry?.is_player || false);
  const [gm, setGm] = useState(entry?.is_gm || false);
  const [notes, setNotes] = useState(entry?.notes || "");
  const [scenarioError, setScenarioError] = useState(false);
  return <form id={formId} className="tracker-form" onSubmit={async (event) => {
    event.preventDefault();
    if (!scenario) { setScenarioError(true); return; }
    if (await onSave({ scenario: Number(scenario), is_player: player, is_gm: gm, notes })) {
      if (!entry) { setScenario(""); setPlayer(false); setGm(false); setNotes(""); }
    }
  }}>
    <ScenarioField value={scenario} invalid={scenarioError}
      onChange={(value) => { setScenario(value); setScenarioError(false); }} scenarios={scenarios} disabled={busy} />
    <fieldset className="tracker-checks tracker-wide"><legend>Participación</legend>
      <label><input type="checkbox" checked={player} onChange={(event) => { setPlayer(event.target.checked); onRoleChange(event.target.checked || gm); }} disabled={busy} /> Jugador</label>
      <label><input type="checkbox" checked={gm} onChange={(event) => { setGm(event.target.checked); onRoleChange(player || event.target.checked); }} disabled={busy} /> GM</label>
    </fieldset>
    <label className="tracker-wide">Notas del historial<textarea rows="3" value={notes} onChange={(event) => setNotes(event.target.value)} disabled={busy} /></label>
  </form>;
}

function ScenarioFilters({ search, setSearch, season, setSeason, seasons, count, total, label, role, setRole }) {
  return <div className="tracker-credit-filters">
    <label>Buscar {label}<input type="search" placeholder="Buscar por título, temporada o número..." value={search} onChange={(event) => setSearch(event.target.value)} /></label>
    <label>Temporada<select value={season} onChange={(event) => setSeason(event.target.value)}><option value="all">Todas las temporadas</option>{seasons.map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
    {setRole && <label>Rol<select value={role} onChange={(event) => setRole(event.target.value)}><option value="all">Todos</option><option value="player">Jugador</option><option value="gm">GM</option></select></label>}
    {(search || season !== "all" || (setRole && role !== "all")) && <button type="button" className="tracker-secondary" onClick={() => { setSearch(""); setSeason("all"); if (setRole) setRole("all"); }}>Limpiar</button>}
    <small className="tracker-credit-count">{count} de {total} {label} visibles</small>
  </div>;
}

function ScenarioCards({ records, type, busy, onEdit, onDelete }) {
  return <ul className="tracker-records tracker-credit-cards">{records.map((item) => <li key={item.id}>
    <div className="tracker-credit-content"><strong>{type === "credit" && item.kind === "initial" ? "Experiencia inicial" : item.scenario_name}</strong>
      {(type === "history" || item.kind !== "initial") && (item.scenario_season || item.scenario_number) && <small className="tracker-scenario-meta">{[item.scenario_number && `Escenario: ${item.scenario_number}`, item.scenario_season && `Temporada: ${item.scenario_season}`].filter(Boolean).join(" · ")}</small>}
      {type === "credit" ? <small className="tracker-credit-xp">{xp(item.experience)} XP</small> : <div className="tracker-history-roles">{item.is_player && <span>Jugador</span>}{item.is_gm && <span>GM</span>}</div>}
      {item.notes && <p className="tracker-notes">{item.notes}</p>}
    </div>
    <div className="tracker-credit-actions">
      <button type="button" className="tracker-icon-button" aria-label={`Editar ${type === "credit" ? "crédito" : "historial"}: ${item.scenario_name || "Experiencia inicial"}`} title="Editar" disabled={busy} onClick={() => onEdit(item)}><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L9 17l-4 1 1-4Z"/></svg></button>
      <button type="button" className="tracker-icon-button tracker-icon-danger" aria-label={`Eliminar ${type === "credit" ? "crédito" : "historial"}: ${item.scenario_name || "Experiencia inicial"}`} title="Eliminar" disabled={busy} onClick={() => onDelete(item)}><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 10v6M14 10v6"/></svg></button>
    </div>
  </li>)}</ul>;
}

function Progress({ character }) {
  const progress = character.progression;
  return <div className="tracker-progress">
    <strong>Nivel {character.level} · {xp(character.experience)} XP</strong>
    <progress value={progress.percent} max="100" aria-label={`Progreso de ${character.name} hacia nivel ${progress.next_level}`} />
    <small>{xp(character.experience)} / {xp(progress.next_level_experience)} XP para subir a nivel {progress.next_level}. Faltan {xp(progress.remaining_experience)} XP.</small>
  </div>;
}

function Tracker() {
  const { isAuthenticated, loadingAuth, userData } = useContext(AuthContext);
  const [params, setParams] = useSearchParams();
  const characterId = params.get("character");
  const historyTab = params.get("tab") === "history";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  const [creditDialog, setCreditDialog] = useState(null);
  const [bulkStatus, setBulkStatus] = useState({ count: 0, valid: false });
  const [bulkStep, setBulkStep] = useState(1);
  const [bulkDiscard, setBulkDiscard] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(null);
  const [historyCanSubmit, setHistoryCanSubmit] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [characterSearch, setCharacterSearch] = useState("");
  const [characterLevel, setCharacterLevel] = useState("");
  const [characterGame, setCharacterGame] = useState("all");
  const [creditSearch, setCreditSearch] = useState("");
  const [creditSeason, setCreditSeason] = useState("all");
  const [historySearch, setHistorySearch] = useState("");
  const [historySeason, setHistorySeason] = useState("all");
  const [historyRole, setHistoryRole] = useState("all");

  useEffect(() => {
    if (loadingAuth || !isAuthenticated) return;
    let active = true;
    setLoading(true);
    setError("");
    Promise.all([
      trackerRequest("get", "/tracker/characters/"), trackerRequest("get", "/tracker/history/"),
      trackerRequest("get", "/scenarios/"),
      characterId && !historyTab ? trackerRequest("get", `/tracker/characters/${characterId}/credits/`) : Promise.resolve([]),
    ]).then(([characters, history, scenarios, credits]) => {
      if (active) setData({ characters, history, scenarios, credits });
    }).catch((requestError) => { if (active) { setData(null); setError(errorMessage(requestError)); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isAuthenticated, loadingAuth, characterId, historyTab, version]);

  useEffect(() => { setCreditDialog(null); setBulkStep(1); setBulkDiscard(false); setBulkStatus({ count: 0, valid: false }); setHistoryDialog(null); setDeleting(null); setNotice(""); setCreditSearch(""); setCreditSeason("all"); setHistorySearch(""); setHistorySeason("all"); setHistoryRole("all"); }, [characterId, historyTab]);

  async function save(method, path, payload, afterSave) {
    if (busy) return false;
    setBusy(true); setError(""); setNotice("");
    try {
      const result = await trackerRequest(method, path, payload);
      if (afterSave) afterSave(result);
      setVersion((value) => value + 1);
      setNotice(method === "delete" ? "Registro eliminado." : "Cambios guardados.");
      return true;
    } catch (requestError) { setError(errorMessage(requestError)); return false; }
    finally { setBusy(false); }
  }

  const openCreditDialog = (credit = null) => {
    setCreditDialog({ credit, mode: "individual" });
    setBulkStep(1);
    setBulkStatus({ count: 0, valid: false });
    setBulkDiscard(false);
    setDeleting(null);
    setError("");
  };
  const dismissCreditDialog = () => {
    setCreditDialog(null);
    setBulkStep(1);
    setBulkStatus({ count: 0, valid: false });
    setBulkDiscard(false);
    setError("");
  };
  const requestCloseCreditDialog = () => {
    if (busy) return;
    if (!creditDialog?.credit && bulkStatus.count > 0) setBulkDiscard(true);
    else dismissCreditDialog();
  };
  const creditBulkMode = creditDialog?.mode === "bulk" && !creditDialog.credit;

  const levelFilter = characterLevel.trim().match(/^(\d+)\s*(?:-\s*(\d+))?$/);
  const levelMin = levelFilter ? Number(levelFilter[1]) : null;
  const levelMax = levelFilter ? Number(levelFilter[2] ?? levelFilter[1]) : null;
  const characterLevels = [...new Set((data?.characters || []).map((item) => Number(item.level))
    .filter(Number.isFinite))].sort((a, b) => a - b);
  const filteredCharacters = (data?.characters || []).filter((item) => {
    const q = normalized(characterSearch);
    if (q && ![item.name, item.character_class, item.notes].some((value) => matchesCharacterSearch(value, q))) return false;
    if (characterGame !== "all" && item.game !== characterGame) return false;
    if (characterLevel.trim() && (!levelFilter || levelMin > levelMax
      || Number(item.level) < levelMin || Number(item.level) > levelMax)) return false;
    return true;
  });

  const accountPaizoNumber = userData?.paizo_number || "";
  const selected = data?.characters.find((character) => String(character.id) === characterId);
  const characterScenarios = (data?.scenarios || []).filter((scenario) => scenario.game === (selected?.game || "pfs2"));
  const selectableCharacterScenarios = catalogForTracker(characterScenarios);
  const creditSeasons = [...new Set((data?.credits || []).filter((credit) => credit.kind !== "initial" && credit.scenario_season).map((credit) => credit.scenario_season))].sort((a, b) => a.localeCompare(b, "es", { numeric: true }));
  const filteredCredits = (data?.credits || []).filter((credit) => {
    if (creditSeason !== "all" && credit.scenario_season !== creditSeason) return false;
    const query = normalized(creditSearch);
    if (!query) return true;
    const code = numericCode(credit.scenario_season, credit.scenario_number);
    const values = [credit.kind === "initial" ? "Experiencia inicial" : credit.scenario_name,
      credit.scenario_season, credit.scenario_number, code, code && `S${code}`,
      code && `S${code.split("-")[0].padStart(2, "0")}-${code.split("-")[1]}`];
    return values.some((value) => normalized(value).includes(query));
  });
  const historySeasons = [...new Set((data?.history || []).map((entry) => entry.scenario_season).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es", { numeric: true }));
  const filteredHistory = (data?.history || []).filter((entry) => {
    if (historySeason !== "all" && entry.scenario_season !== historySeason) return false;
    if (historyRole === "player" && !entry.is_player) return false;
    if (historyRole === "gm" && !entry.is_gm) return false;
    const query = normalized(historySearch);
    if (!query) return true;
    const code = numericCode(entry.scenario_season, entry.scenario_number);
    return [entry.scenario_name, entry.scenario_season, entry.scenario_number, code,
      code && `S${code}`, code && `S${code.split("-")[0].padStart(2, "0")}-${code.split("-")[1]}`]
      .some((value) => normalized(value).includes(query));
  });
  return <main className="tracker container my-4">
    {loadingAuth ? <p role="status">Comprobando tu sesión...</p> : !isAuthenticated ?
      <p><Link to="/login">Inicia sesión</Link> con tu cuenta de PFS Madrid para ver tus personajes.</p> : <>
        <nav className="tracker-nav" aria-label="Secciones del tracker">
          <Link to="/tracker/" aria-current={!historyTab ? "page" : undefined}>Personajes</Link>
          <Link to="/tracker/?tab=history" aria-current={historyTab ? "page" : undefined}>Historial Player/GM</Link>
        </nav>
        {error && !showCreate && !creditDialog && !historyDialog && !deleting && <div className="tracker-error" role="alert">{error} <button onClick={() => setVersion((value) => value + 1)} disabled={busy}>Reintentar carga</button> <Link to="/login">Iniciar sesión</Link></div>}
        {notice && <p role="status">{notice}</p>}
        {loading ? <p role="status">Cargando tracker...</p> : data && <>
          {!historyTab && !characterId && <>
            <div className="tracker-section-heading">
              <h2>Mis personajes</h2>
              <button type="button" onClick={() => { setError(""); setShowCreate(true); }} disabled={busy}>Añadir PJ</button>
            </div>
            {!!data.characters.length && <>
              <div className="tracker-character-filters">
                <label className="tracker-character-search">Buscar personaje
                  <input type="search" placeholder="Nombre, clase o notas..." value={characterSearch}
                    onChange={(event) => setCharacterSearch(event.target.value)} />
                </label>
                <label>Nivel o tier
                  <input type="text" inputMode="numeric" list="tracker-character-levels"
                    placeholder="Ej. 7 o 7-10" value={characterLevel}
                    onChange={(event) => setCharacterLevel(event.target.value)} />
                  <datalist id="tracker-character-levels">
                    {characterLevels.map((level) => <option key={level} value={String(level)} />)}
                  </datalist>
                </label>
                <label>Sistema
                  <select value={characterGame} onChange={(event) => setCharacterGame(event.target.value)}>
                    <option value="all">Todos</option>
                    <option value="pfs2">Pathfinder 2e</option>
                    <option value="sfs2">Starfinder 2e</option>
                  </select>
                </label>
              </div>
              <div className="tracker-character-filter-count" role="status">
                Mostrando {filteredCharacters.length} de {data.characters.length} personajes
              </div>
            </>}
            <div className="tracker-grid">{filteredCharacters.map((character) => <article className="tracker-card tracker-character-card" key={character.id}>
              <Link className="tracker-character-card-link" to={`/tracker/?character=${character.id}`}>
                <h3>{character.name}</h3>
                <p>{[character.character_class, character.paizo_id || "Sin número PFS",
                  character.game === "sfs2" ? "Starfinder 2e" : "Pathfinder 2e"].filter(Boolean).join(" · ")}</p>
                <Progress character={character} />
              </Link>
              {!!character.notes?.trim() && <details className="tracker-character-notes">
                <summary><span className="tracker-character-notes-chevron" aria-hidden="true">▸</span><span className="tracker-character-notes-label">Ver notas</span></summary>
                <p>{character.notes}</p>
              </details>}
            </article>)}</div>
            {!data.characters.length && <p>Todavía no tienes personajes.</p>}
            {!!data.characters.length && !filteredCharacters.length && <p>No se encontraron personajes.</p>}
          </>}
          {!historyTab && characterId && (!selected ? <p role="alert">No se encuentra el personaje. <Link to="/tracker/">Volver al listado</Link></p> : <>
            <Link className="tracker-back" to="/tracker/"><span aria-hidden="true">←</span> Volver a personajes</Link>
            <section className="tracker-panel"><h2>{selected.name}</h2><p className="tracker-character-game">{selected.game === "sfs2" ? "Starfinder 2e" : "Pathfinder 2e"}</p><Progress character={selected} />
              <details><summary>Editar personaje</summary>
                <CharacterForm key={`${selected.id}-${version}`} character={selected} paizoNumber={accountPaizoNumber} busy={busy} onSave={(form) => save("patch", `/tracker/characters/${selected.id}/`, form)} />
              </details>
            </section>
            <section className="tracker-panel">
              <div className="tracker-section-heading">
                <h2>Créditos de XP</h2>
                <button type="button" disabled={busy} onClick={() => openCreditDialog()}>Añadir crédito</button>
              </div>
              <p>Estos créditos no cambian las marcas Player/GM de tu historial.</p>
              {!!data.credits.length && <ScenarioFilters search={creditSearch} setSearch={setCreditSearch} season={creditSeason} setSeason={setCreditSeason} seasons={creditSeasons} count={filteredCredits.length} total={data.credits.length} label="créditos" />}
              {!data.credits.length && <p>Este personaje todavía no tiene créditos.</p>}
              {!!data.credits.length && !filteredCredits.length && <p>No hay créditos que coincidan con los filtros.</p>}
              <ScenarioCards records={filteredCredits} type="credit" busy={busy}
                onEdit={(credit) => openCreditDialog(credit)}
                onDelete={(credit) => { setCreditDialog(null); setDeleting({ type: "credit", item: credit }); setError(""); }} />
            </section>
          </>)}
          {historyTab && <section className="tracker-panel">
            <div className="tracker-section-heading">
              <h2>Historial Player/GM</h2>
              <button type="button" disabled={busy} onClick={() => { setHistoryDialog({ entry: null }); setHistoryCanSubmit(false); setDeleting(null); setError(""); }}>Añadir al historial</button>
            </div>
            <p>Indica los escenarios que has jugado o dirigido. No asigna XP a ningún personaje.</p>
            {!!data.history.length && <ScenarioFilters search={historySearch} setSearch={setHistorySearch} season={historySeason} setSeason={setHistorySeason} seasons={historySeasons} count={filteredHistory.length} total={data.history.length} label="escenarios" role={historyRole} setRole={setHistoryRole} />}
            {!data.history.length && <p>Todavía no tienes escenarios en el historial nuevo.</p>}
            {!!data.history.length && !filteredHistory.length && <p>No hay escenarios que coincidan con los filtros.</p>}
            <ScenarioCards records={filteredHistory} type="history" busy={busy}
              onEdit={(entry) => { setHistoryDialog({ entry }); setHistoryCanSubmit(entry.is_player || entry.is_gm); setDeleting(null); setError(""); }}
              onDelete={(entry) => { setHistoryDialog(null); setDeleting({ type: "history", item: entry }); setError(""); }} />
          </section>}

        </>}
        {creditDialog && selected && data && <Modal show centered scrollable
          size={creditBulkMode ? "lg" : undefined}
          className={creditBulkMode ? "tracker-modal-shell tracker-bulk-modal" : "tracker-modal-shell"}
          onHide={requestCloseCreditDialog}
          backdrop={busy ? "static" : true} keyboard={!busy} aria-labelledby="tracker-credit-dialog-title">
          <Modal.Header closeButton={!busy && !bulkDiscard}>
            <div className="tracker-modal-title-and-tabs">
              <Modal.Title id="tracker-credit-dialog-title">
                {creditDialog.credit ? "Editar crédito" : "Añadir créditos"}
              </Modal.Title>
              {!creditDialog.credit && !bulkDiscard && <div className="tracker-credit-mode-tabs" role="tablist" aria-label="Modo de añadir créditos">
                <button type="button" id="tracker-credit-individual-tab" role="tab"
                  aria-controls="tracker-credit-individual-panel" aria-selected={!creditBulkMode}
                  disabled={busy} onClick={() => { setCreditDialog({ credit: null, mode: "individual" }); setError(""); }}>
                  Individual
                </button>
                <button type="button" id="tracker-credit-bulk-tab" role="tab"
                  aria-controls="tracker-credit-bulk-panel" aria-selected={creditBulkMode}
                  disabled={busy} onClick={() => { setCreditDialog({ credit: null, mode: "bulk" }); setError(""); }}>
                  En bloque
                </button>
              </div>}
            </div>
          </Modal.Header>
          <Modal.Body className="tracker tracker-entry-modal">
            {bulkDiscard && <div className="tracker-bulk-discard-panel" role="alertdialog" aria-label="Descartar créditos sin guardar">
              <h3>¿Descartar créditos?</h3>
              <p>Hay <strong>{bulkStatus.count} {bulkStatus.count === 1 ? "crédito sin guardar" : "créditos sin guardar"}</strong>. Si sales ahora, se perderán.</p>
            </div>}
            <div hidden={bulkDiscard}>
              <p className="tracker-dialog-context">Personaje: <strong>{selected.name}</strong></p>
              {error && <p className="tracker-error" role="alert">{error}</p>}
              <div id="tracker-credit-individual-panel" role="tabpanel" aria-labelledby="tracker-credit-individual-tab" hidden={creditBulkMode}>
                <CreditForm key={creditDialog.credit?.id || "new"} formId="tracker-credit-form"
                  credit={creditDialog.credit} scenarios={creditDialog.credit ? characterScenarios : selectableCharacterScenarios} busy={busy}
                  onSave={(form) => save(creditDialog.credit ? "patch" : "post",
                    `/tracker/characters/${selected.id}/credits/${creditDialog.credit ? `${creditDialog.credit.id}/` : ""}`,
                    form, () => {
                      if (bulkStatus.count > 0) setCreditDialog({ credit: null, mode: "bulk" });
                      else dismissCreditDialog();
                    })} />
              </div>
              {!creditDialog.credit && <div id="tracker-credit-bulk-panel" role="tabpanel" aria-labelledby="tracker-credit-bulk-tab"
                hidden={!creditBulkMode}>
                <TrackerBulkCredits formId="tracker-bulk-form" character={selected}
                  scenarios={selectableCharacterScenarios} existingCredits={data.credits} busy={busy}
                  onStatusChange={setBulkStatus} step={bulkStep} onStepChange={setBulkStep}
                  onSave={(payload) => save("post", `/tracker/characters/${selected.id}/credits/bulk/`, payload,
                    () => dismissCreditDialog())} />
              </div>}
            </div>
          </Modal.Body>
          <Modal.Footer className="tracker tracker-modal-footer tracker-credit-modal-footer">
            {bulkDiscard ? <>
              <button type="button" className="tracker-secondary" disabled={busy}
                onClick={() => setBulkDiscard(false)}>Seguir editando</button>
              <button type="button" className="tracker-danger" disabled={busy}
                onClick={dismissCreditDialog}>Descartar créditos</button>
            </> : creditBulkMode ? <>
              {bulkStep === 1 ? <div className="tracker-bulk-footer-selection">
                {bulkStatus.count} {bulkStatus.count === 1 ? "crónica seleccionada" : "crónicas seleccionadas"} · Siguiente paso: configurar XP
              </div> : <div className="tracker-bulk-footer-totals" aria-label="Resumen de créditos pendientes">
                <span><small>XP nuevas</small><strong>{xp(bulkStatus.addedXP || 0)} XP</strong></span>
                <span><small>XP resultante</small><strong>{xp(bulkStatus.resultingXP ?? selected.experience)} XP</strong></span>
                <span><small>Nivel</small><strong>{bulkStatus.resultingLevel ?? selected.level}</strong></span>
              </div>}
              <div className="tracker-bulk-footer-actions">
                <button type="button" className="tracker-secondary" disabled={busy}
                  onClick={() => {
                    if (bulkStep === 1) requestCloseCreditDialog();
                    else setBulkStep((current) => current - 1);
                  }}>
                  {bulkStep === 1 ? "Cancelar" : bulkStep === 2 ? "Anterior" : "Volver atrás"}
                </button>
                <button type="submit" form="tracker-bulk-form"
                  disabled={busy || (bulkStep === 1 ? bulkStatus.count === 0 : !bulkStatus.valid)
                    || (bulkStep === 3 && bulkStatus.hasInitial && !bulkStatus.acknowledged)}>
                  {bulkStep === 1 ? "Siguiente" : bulkStep === 2 ? "Continuar" : "Finalizar"}
                </button>
              </div>
            </> : <>
              <button type="button" className="tracker-secondary" disabled={busy}
                onClick={requestCloseCreditDialog}>Cancelar</button>
              <button type="submit" form="tracker-credit-form" disabled={busy}>
                {creditDialog.credit ? "Guardar crédito" : "Crear crédito"}
              </button>
            </>}
          </Modal.Footer>
        </Modal>}
        {historyDialog && data && <Modal show centered scrollable className="tracker-modal-shell"
          onHide={() => { if (!busy) { setHistoryDialog(null); setError(""); } }}
          backdrop={busy ? "static" : true} keyboard={!busy} aria-labelledby="tracker-history-dialog-title">
          <Modal.Header closeButton={!busy}><Modal.Title id="tracker-history-dialog-title">
            {historyDialog.entry ? "Editar historial" : "Añadir al historial"}
          </Modal.Title></Modal.Header>
          <Modal.Body className="tracker tracker-entry-modal">
            {error && <p className="tracker-error" role="alert">{error}</p>}
            <HistoryForm key={historyDialog.entry?.id || "new"} formId="tracker-history-form"
              entry={historyDialog.entry} scenarios={data.scenarios} busy={busy} onRoleChange={setHistoryCanSubmit}
              onSave={(form) => save(historyDialog.entry ? "patch" : "post",
                `/tracker/history/${historyDialog.entry ? `${historyDialog.entry.id}/` : ""}`,
                form, () => setHistoryDialog(null))} />
          </Modal.Body>
          <Modal.Footer className="tracker tracker-modal-footer">
            <button type="button" className="tracker-secondary" disabled={busy}
              onClick={() => { setHistoryDialog(null); setError(""); }}>Cancelar</button>
            <button type="submit" form="tracker-history-form" disabled={busy || !historyCanSubmit}>
              {historyDialog.entry ? "Guardar historial" : "Añadir al historial"}
            </button>
          </Modal.Footer>
        </Modal>}
        {deleting && <Modal show centered className="tracker-modal-shell"
          onHide={() => { if (!busy) { setDeleting(null); setError(""); } }}
          backdrop={busy ? "static" : true} keyboard={!busy} aria-labelledby="tracker-delete-dialog-title">
          <Modal.Header closeButton={!busy}><Modal.Title id="tracker-delete-dialog-title">
            {deleting.type === "credit" ? "Eliminar crédito de XP" : "Eliminar del historial"}
          </Modal.Title></Modal.Header>
          <Modal.Body className="tracker">
            {error && <p className="tracker-error" role="alert">{error}</p>}
            {deleting.type === "credit" ? <>
              <p>¿Eliminar el crédito <strong>{deleting.item.kind === "initial" ? "Experiencia inicial" : deleting.item.scenario_name}</strong> de <strong>{selected?.name}</strong>?</p>
              <p>Se retirarán <strong>{xp(deleting.item.experience)} XP</strong> del personaje y se recalculará su progreso. Esta acción no se puede deshacer.</p>
            </> : <>
              <p>¿Eliminar <strong>{deleting.item.scenario_name}</strong> de tu historial Player/GM?</p>
              <p>Se borrarán sus marcas de Jugador/GM y sus notas. Los créditos de XP de los personajes no se verán afectados.</p>
            </>}
          </Modal.Body>
          <Modal.Footer className="tracker tracker-modal-footer tracker-delete-actions">
            <button type="button" className="tracker-secondary" disabled={busy} onClick={() => { setDeleting(null); setError(""); }}>Cancelar</button>
            <button type="button" className="tracker-danger" disabled={busy}
              onClick={() => save("delete", deleting.type === "credit"
                ? `/tracker/characters/${selected.id}/credits/${deleting.item.id}/`
                : `/tracker/history/${deleting.item.id}/`, undefined, () => setDeleting(null))}>
              {deleting.type === "credit" ? "Eliminar crédito" : "Eliminar del historial"}
            </button>
          </Modal.Footer>
        </Modal>}
        {showCreate && <Modal show centered scrollable className="tracker-modal-shell"
          onHide={() => { if (!busy) { setShowCreate(false); setError(""); } }}
          backdrop={busy ? "static" : true} keyboard={!busy} aria-labelledby="tracker-create-title">
          <Modal.Header closeButton={!busy}><Modal.Title id="tracker-create-title">Crear nuevo personaje</Modal.Title></Modal.Header>
          <Modal.Body className="tracker tracker-entry-modal">
            {error && <p className="tracker-error" role="alert">{error}</p>}
            <CharacterForm busy={busy} paizoNumber={accountPaizoNumber} formId="tracker-create-character-form"
              onSave={(form) => save("post", "/tracker/characters/", form, (created) => {
                setShowCreate(false); setParams({ character: String(created.id) });
              })} />
          </Modal.Body>
          <Modal.Footer className="tracker tracker-modal-footer">
            <button type="button" className="tracker-secondary" disabled={busy}
              onClick={() => { setShowCreate(false); setError(""); }}>Cancelar</button>
            <button type="submit" form="tracker-create-character-form" disabled={busy || !accountPaizoNumber}>Crear personaje</button>
          </Modal.Footer>
        </Modal>}
      </>}
  </main>;
}

export default Tracker;
